import { ActionTree } from 'vuex';
import { RootState, SingleFilter } from '@/store/types';
import api from '@/api';
import * as goalRequests from '@/api/requests/evaluator/goals';
import * as ratingRequests from '@/api/requests/evaluator/ratings';
import * as packageRequests from "@/api/requests/evaluator/packages";
import * as evaluationRequests from "@/api/requests/evaluator/evaluations";
import { GoalResource, PackageResource, EvaluationResource } from '@/api/requests/evaluator/types';
import { EvaluatorState } from './types';
import Goal from '@/api/models/goal';
import Package from '@/api/models/package';
import {
    GoalMediaCreateRequestPayload,
    GoalMediaDeleteRequestPayload,
    GoalMediaUpdateRequestPayload,
    PackageMediaCreateRequestPayload,
    PackageMediaUpdateRequestPayload,
    PackageMediaDeleteRequestPayload

} from '@/store/modules/evaluator/types';
import { MediaResource } from '@/api/requests/types';
import Evaluation from '@/api/models/evaluation';

export const actions: ActionTree<EvaluatorState, RootState> = {

    /** Goals */

    /**
     * Fetch all goals.
     *
     * @param commit
     * @param getters
     */
    async fetchGoals({ commit, getters }) {
        const response = await api.call(goalRequests.ALL(getters.httpFilters));
        commit('setGoals', response.data.data);
    },

    /**
     * Create a new goal.
     *
     * @param commit
     * @param goal
     */
    async createGoal({ commit }, goal: Goal) {
        const response = await api.call(goalRequests.STORE(goal.toRequest()));
        const resource: GoalResource = response.data.data;
        commit('addGoal', resource);
    },

    /**
     * Delete goal by id.
     *
     * @param commit
     * @param goalId
     */
    async deleteGoal({ commit }, goalId: number) {
        await api.call(goalRequests.DELETE(goalId));
        commit('removeGoal', goalId);
    },

    /**
     * Update an existing goal.
     *
     * @param commit
     * @param goal
     */
    async updateGoal({ commit }, goal: Goal) {
        if (!goal.id) {
            throw new Error('No goal id provided.');
        }
        const response = await api.call(goalRequests.UPDATE(goal.id, goal));
        const resource: GoalResource = response.data.data;
        commit('updateGoal', resource);
    },

    /**
     * Set active goal.
     *
     * @param commit
     * @param getters
     * @param goalId
     */
    async setActiveGoal({ commit, getters }, goalId: number) {
        if (!getters.isGoalLoaded(goalId)) {
            const response = await api.call(goalRequests.FIND(goalId));
            commit('upsertGoal', response.data.data);
        }
        commit('setActiveGoalId', goalId);
    },

    /** Evaluations */

    /**
     * Fetch all evaluations.
     *
     * @param commit
     * @param getters
     */
    async fetchEvaluations({ commit, getters }, {packageId}) {
        const response = await api.call(evaluationRequests.ALL(packageId, getters.httpFilters));
        commit('setEvaluations', response.data.data);
    },

    /**
     * Create or update evaluations.
     *
     * @param commit
     * @param evaluation
     */
    async upsertEvaluation({ commit, dispatch }, {packageId, evaluation}) {
        const response = await api.call(evaluationRequests.UPSERT(packageId, evaluation.toRequest()));
        const resource: EvaluationResource = response.data.data;
        dispatch('fetchEvaluation', {packageId})
    },

    /**
     * Delete evaluation by id.
     *
     * @param commit
     * @param evaluationId
     */
    async deleteEvaluation({ commit }, {packageId, evaluationId}) {
        await api.call(evaluationRequests.DELETE(packageId, evaluationId));
        commit('removeEvaluation', evaluationId);
    },
    
    /* Packages */

    /**
     * Fetch all packages.
     *
     * @param commit
     * @param getters
     */
     async fetchPackages({ commit, getters }) {
        const response = await api.call(packageRequests.ALL(getters.httpFilters));
        commit('setPackages', response.data.data);
    },

    /**
     * Create a new package.
     *
     * @param commit
     * @param singlePackage
     */
    async createPackage({ commit }, singlePackage: Package) {
        const response = await api.call(packageRequests.STORE(singlePackage.toRequest()));
        const resource: PackageResource = response.data.data;
        commit('addPackage', resource);
    },

    /**
     * Delete package by id.
     *
     * @param commit
     * @param packageId
     */
    async deletePackage({ commit }, packageId: number) {
        await api.call(packageRequests.DELETE(packageId));
        commit('removePackage', packageId);
    },

    /**
     * Update an existing package.
     *
     * @param commit
     * @param singlePackage
     */
    async updatePackage({ commit }, singlePackage: Package) {
        if (!singlePackage.id) {
            throw new Error('No package id provided.');
        }
        const response = await api.call(packageRequests.UPDATE(singlePackage.id, singlePackage));
        const resource: PackageResource = response.data.data;
        commit('updatePackage', resource);
    },
    
    /**
     * Set visible on pacakges of the year 
     *
     * @param commit
     * @param getters
     */
    async setVisibleAnnualPackages() {
        const response = await api.call(packageRequests.VISIBLE_ANNUAL());
    },
    
    /**
     * Confirm acknowledgment on package 
     *
     * @param commit
     * @param getters
     */
     async confirmAcknowledgmentPackage({ commit }, singlePackage: Package) {
        if (!singlePackage.id) {
            throw new Error('No package id provided.');
        }
        const response = await api.call(packageRequests.CONFIRM_ACKNOWLEDGMENT(singlePackage.id));
        const resource: PackageResource = response.data.data;
        commit('updatePackage', resource);
    },

    /** Ratings */
    
    /**
     * Fetch all ratings.
     *
     * @param commit
     * @param getters
     */
     async fetchRatings({ commit, getters }) {
        const response = await api.call(ratingRequests.ALL());
        commit('setRatings', response.data.data);
    },


    /** Filters */

    /**
     * Reset a single filter.
     *
     * @param commit
     * @param dispatch
     * @param key
     */
     async resetFilter({ commit, dispatch }, key: string) {
        await commit('resetFilter', key);
        dispatch('fetchGoals');
    },

    /**
     * Set filters.
     *
     * @param commit
     * @param dispatch
     * @param filters
     */
     setFilters({ commit, dispatch }, filters: SingleFilter[]) {
        filters.map((filter) => commit('setFilter', filter));
        dispatch('fetchGoals');
    },

    /* Goal Media */

    /**
     * Add a media to a goal.
     *
     * @param commit
     * @param goalId
     * @param file
     */
    async addMediaToGoal({ commit }, { goalId, file }: GoalMediaCreateRequestPayload) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.call(goalRequests.ADD_MEDIA(goalId, formData, (progressEvent) => {
            // TODO: gestione progress upload.
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(percentCompleted);
        }));
        commit('addMediaToGoal', { goalId, media: response.data.data });
    },

    /**
     * Update a media into a goal.
     *
     * @param commit
     * @param goalId
     * @param mediaId
     * @param media
     */
    async updateMediaIntoGoal({ commit }, { goalId, mediaId, media }: GoalMediaUpdateRequestPayload) {
        const response = await api.call(goalRequests.UPDATE_MEDIA(goalId, mediaId, media));
        commit('addMediaToGoal', { goalId, media: response.data.data });
    },

    /**
     * Remove a media from a goal.
     *
     * @param commit
     * @param goalId
     * @param mediaId
     */
    async removeMediaFromGoal({ commit }, { goalId, mediaId }: GoalMediaDeleteRequestPayload) {
        await api.call(goalRequests.REMOVE_MEDIA(goalId, mediaId));
        commit('removeMediaFromGoal', { goalId, mediaId });
    },

    /**
     * Download a media of a goal.
     *
     * @param commit
     * @param getters
     * @param goalId
     * @param mediaId
     */
    async downloadMediaOfGoal({ commit, getters }, { goalId, mediaId }: GoalMediaDeleteRequestPayload) {
        // Retrieve goal
        const goal = getters.getGoalById(goalId);

        // Retrieve media
        const media = goal.media.find((item: MediaResource) => item.id === mediaId);

        if (!media) {
            throw new Error('No media to download');
        }

        const response = await api.call(goalRequests.DOWNLOAD_MEDIA(goalId, mediaId));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', media.fileName);
        document.body.appendChild(link);
        link.click();
    },

    /* Package Media */

    /**
     * Add a media to a package.
     *
     * @param commit
     * @param packageId
     * @param file
     */
     async addMediaToPackage({ commit }, { packageId, file }: PackageMediaCreateRequestPayload) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.call(packageRequests.ADD_MEDIA(packageId, formData, (progressEvent) => {
            // TODO: gestione progress upload.
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(percentCompleted);
        }));
        commit('addMediaToPackage', { packageId, media: response.data.data });
    },

    /**
     * Update a media into a package.
     *
     * @param commit
     * @param packageId
     * @param mediaId
     * @param media
     */
    async updateMediaIntoPackage({ commit }, { packageId, mediaId, media }: PackageMediaUpdateRequestPayload) {
        const response = await api.call(packageRequests.UPDATE_MEDIA(packageId, mediaId, media));
        commit('addMediaToPackage', { packageId, media: response.data.data });
    },

    /**
     * Remove a media from a package.
     *
     * @param commit
     * @param packageId
     * @param mediaId
     */
    async removeMediaFromPackage({ commit }, { packageId, mediaId }: PackageMediaDeleteRequestPayload) {
        await api.call(goalRequests.REMOVE_MEDIA(packageId, mediaId));
        commit('removeMediaFromPackage', { packageId, mediaId });
    },

    /**
     * Download a media of a package.
     *
     * @param commit
     * @param getters
     * @param packageId
     * @param mediaId
     */
    async downloadMediaOfPackage({ commit, getters }, { packageId, mediaId }: PackageMediaDeleteRequestPayload) {
        // Retrieve package
        const singlePackage = getters.getPackageById(packageId);

        // Retrieve media
        const media = singlePackage.media.find((item: MediaResource) => item.id === mediaId);

        if (!media) {
            throw new Error('No media to download');
        }

        const response = await api.call(packageRequests.DOWNLOAD_MEDIA(packageId, mediaId));
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', media.fileName);
        document.body.appendChild(link);
        link.click();
    },
};
